import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { normalizeMessage, normalizeMessages } from '../utils/offerNormalize';
import { EMPTY_BID_MESSAGES } from './useBids';
import { invalidateNotificationQueries } from '../utils/notificationHelpers';
import { LIVE_POLL, LIVE_STALE_MS, livePollInterval } from '../config/liveSync';

export const useConversationDetail = (conversationId, options = {}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: async () => {
      const response = await apiService.getConversationDetail(conversationId);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      return null;
    },
    enabled: !!isAuthenticated && !!conversationId && enabled,
    staleTime: LIVE_STALE_MS,
    refetchInterval: () =>
      conversationId && enabled ? livePollInterval(LIVE_POLL.BADGE) : false,
  });
};

export const useConversationMessages = (conversationId, options = {}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { enablePolling = true, pollingInterval = LIVE_POLL.CHAT } = options;

  return useQuery({
    queryKey: ['conversationMessages', conversationId],
    queryFn: async () => {
      const convResponse = await apiService.getConversationDetail(conversationId);
      const messagesResponse = await apiService.getConversationMessages(conversationId);
      const messages =
        messagesResponse.status === 'success' && messagesResponse.data
          ? normalizeMessages(messagesResponse.data)
          : EMPTY_BID_MESSAGES;

      try {
        await apiService.markConversationRead(conversationId);
      } catch (readErr) {
        console.warn('Mark read failed:', readErr);
      }

      return {
        messages,
        conversation: convResponse.data || null,
        conversationId,
      };
    },
    enabled: !!isAuthenticated && !!conversationId,
    staleTime: 2_000,
    structuralSharing: false,
    refetchInterval:
      enablePolling && conversationId
        ? () => livePollInterval(pollingInterval)
        : false,
    placeholderData: (previous) =>
      previous ?? { messages: EMPTY_BID_MESSAGES, conversation: null, conversationId },
    retry: false,
  });
};

export const useSendConversationMessage = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const buyerId = user?.id || user?._id;

  return useMutation({
    mutationFn: async ({ conversationId, text, productId }) => {
      const response = await apiService.sendConversationMessage(conversationId, text, productId);
      if (response.status !== 'success') {
        throw new Error(response.message || 'Failed to send message');
      }
      return response.data;
    },
    onMutate: async (variables) => {
      const key = ['conversationMessages', variables.conversationId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      const optimistic = normalizeMessage({
        id: `optimistic-${Date.now()}`,
        type: 'TEXT',
        text: variables.text,
        createdAt: new Date().toISOString(),
        sender: buyerId,
        senderId: buyerId,
      });
      if (optimistic && previous) {
        queryClient.setQueryData(key, {
          ...previous,
          messages: [...(previous.messages || []), optimistic],
        });
      }
      return { previous };
    },
    onError: (_err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ['conversationMessages', variables.conversationId],
          context.previous
        );
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['conversationMessages', variables.conversationId],
      });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      invalidateNotificationQueries(queryClient);
    },
  });
};
